import { useEffect, useState, useRef } from "react";
import { Spin, Button, Tag, Typography, Flex, Carousel } from "antd";
import { useNavigate } from "react-router-dom";
import {
  EyeOutlined,
  PhoneOutlined,
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";

const { Title, Paragraph } = Typography;

const ExploreTopThingsToDoDubai = () => {
  const [toursPackage, setToursPackage] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const carouselRef = useRef(null);

  useEffect(() => {
    fetchTours(1, 4);
  }, []);

  const fetchTours = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/product-details?filters[product_groups][name][$eq]=top_things_to_do_in_dubai&fields[0]=title&fields[1]=slug&fields[2]=final_price&fields[3]=old_price&fields[4]=save_price&fields[5]=duration&populate[images][fields][0]=url&populate[sub_product][fields][0]=slug`,
      );
      const newData = res.data.data || [];
      const pagination = res.data.meta?.pagination;

      setPageCount(pagination?.pageCount || 1);

      setToursPackage((prev) =>
        pageNumber === 1 ? newData : [...prev, ...newData],
      );

      setPage(pageNumber);
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-10">
        <Spin size="large" tip="Loading Travelers’ Favorite Choices..." />
      </div>
    );

  return (
    
      <div className="">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-normal text-primary">
              Explore <span className="text-danger"> Europe</span>
            </h2>
            <p className="font-normal mt-1">
              Culture, nature, thrills, and record-breaking experiences
            </p>
          </div>
          <a
            onClick={() => navigate("/view-all/travelers_favorite_choices")}
            className="font-normal text-xs"
          >
            View More <DoubleRightOutlined />
          </a>
        </div>

        {/* ✅ CAROUSEL START */}
        <div className="relative custom-slider">
          {/* LEFT */}
          <button
            onClick={() => carouselRef.current.prev()}
            className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:scale-110 transition"
          >
            <LeftOutlined />
          </button>

          {/* RIGHT */}
          <button
            onClick={() => carouselRef.current.next()}
            className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:scale-110 transition"
          >
            <RightOutlined />
          </button>

          <Carousel
            ref={carouselRef}
            dots={false}
            infinite={false}
            slidesToShow={4}
            slidesToScroll={1}
            className="[&_.slick-slide>div]:h-full [&_.slick-slide]:h-auto"
            responsive={[
              { breakpoint: 1024, settings: { slidesToShow: 3 } },
              { breakpoint: 768, settings: { slidesToShow: 2 } },
              { breakpoint: 480, settings: { slidesToShow: 1 } },
            ]}
          >
            {toursPackage.length > 0 ? (
              toursPackage.map((tour) => {
                const title =
                  tour.title || tour.attributes?.title || "Untitled Tour";
                const subSlug = tour.sub_product?.slug || "not found";
                const detailSlug =
                  tour.slug ||
                  tour.attributes?.slug ||
                  tour.title?.toLowerCase().replace(/\s+/g, "-") ||
                  "details";

                const type = tour.type;
                const final_price = tour.final_price;
                const old_price = tour.old_price;
                const save_price = tour.save_price;
                const duration = tour.duration || tour.attributes?.duration;

                const imageUrl =
                  tour.images?.[0]?.url ||
                  tour.attributes?.images?.data?.[0]?.attributes?.url ||
                  "https://via.placeholder.com/400x250?text=No+Image";

                return (
                  <div key={tour.id} className="px-1 h-full">
                    <div className="h-full flex">
                    <div onClick={() =>
                              navigate(`/${subSlug}/${detailSlug}`)
                            } className="bg-white flex flex-col w-full h-full rounded-xl overflow-hidden shadow-custom hover:shadow-lg">
                      <div
                        className="relative cursor-pointer"
                        onClick={() => navigate(`/${subSlug}/${detailSlug}`)}
                      >
                        <div className="absolute overflow-hidden bg-black/20 w-full h-full">
                          <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                            <div className="text-base font-semibold text-white">
                              AED {final_price}
                            </div>
                            <div className="line-through text-white text-md">
                              AED {old_price}
                            </div>
                            <div className="text-white text-md">
                              AED {save_price}
                            </div>
                          </div>
                        </div>

                        <img
                          src={`${process.env.REACT_APP_API_URL}${imageUrl}`}
                          alt={title}
                          className="w-full h-[170px] object-cover"
                        />

                        <div className="bg-primary absolute bottom-0 translate-y-1/2 right-6 rounded-full shadow-lg text-white text-sm flex justify-center w-[80px] h-[80px]">
                          <div className="text-center mt-2">
                            <div className="text-3xl">★</div>
                            <div className="text-xs">4.9 (523)</div>
                          </div>
                        </div>
                      </div>

                      <div className="p-5 pt-3 flex flex-col flex-grow">
                        <div className="mt-3 mb-2 text-muted text-[11px] font-medium">
                          Tour Type
                          <Tag className="bg-blue-100 text-black border-0 text-[11px] ml-2">
                            {type}
                          </Tag>
                        </div>

                        <div className="font-medium text-black text-md line-clamp-2">
                          {title}
                        </div>

                        <div className="text-primary mt-2 font-medium">
                          {duration}
                        </div>

                        {/* <Flex className="mt-4 gap-3" vertical>
                          <Button
                            type="danger"
                            size="small"
                            icon={<EyeOutlined />}
                            className="font-medium flex-1 py-3 text-xs bg-danger hover:bg-red-700 border-none text-white w-full"
                            onClick={() =>
                              navigate(`/${subSlug}/${detailSlug}`)
                            }
                          >
                            View Details
                          </Button>

                           
                        </Flex> */}
                      </div>
                    </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p>No tours available.</p>
            )}
          </Carousel>
        </div>

        {/* LOAD MORE */}
        {page < pageCount && (
          <div className="text-center mt-6">
            <Button
              onClick={() => {
                const nextPage = page + 1;
                setPage(nextPage);
                fetchTours(nextPage);
              }}
              loading={loading}
              className="w-60 py-6 text-sm"
            >
              {loading ? "Loading..." : "See More"}
            </Button>
          </div>
        )}
      </div>
   
  );
};

export default ExploreTopThingsToDoDubai;
